import { WorkItemFactory } from '../workitem-factory';
import { UserPermissionService } from 'shared/services/user-permissions/user-permission.service';
import { ActionPermissionType } from '../../common/constants/permission-type-enum';
import { Settings } from '../../settings/settings.interface';
import { WorkItemChatPinInvite } from '../models/workitem-chat-pin-invite-model';
import { BcSettingsService } from '../../settings/settings.service';
import { WorkItemTypes } from '../constants/workitem-types';
import { PinType } from '../constants/pin-types';
import { BcRestApi } from '../../api/rest-api';
import { Injectable } from '@angular/core';
import { LoginSubscriber } from 'shared/services/subscribers/login-subscriber.service';
import { LogoutSubscriber } from 'shared/services/subscribers/logout-subscriber.service';

export const MAX_CHAT_PIN_INVITE_WORK_ITEM_COUNT = 1;

@Injectable()
export class PinviteList {
  public chatPinviteWorkItemList: WorkItemChatPinInvite[] = [];

  private _isPinInvitationIsConfiguredForAccount: boolean = false;

  constructor(private userPermissionService: UserPermissionService,
              private settingsService: BcSettingsService,
              private bcRestApi: BcRestApi,
              LoginSubscriber: LoginSubscriber,
              LogoutSubscriber: LogoutSubscriber) {
    LoginSubscriber.subscribe(this._preparePinvites.bind(this));
    LogoutSubscriber.subscribe(this.reset.bind(this));
  }

  public reset() {
    this.chatPinviteWorkItemList.length = 0;
  }

  public get pinviteWorkItems(): WorkItemChatPinInvite[] {
    return this.chatPinviteWorkItemList;
  }

  public canCreatePinvite(): boolean {
    return this._isPinInvitationIsConfiguredForAccount && this.pinviteWorkItems.length < MAX_CHAT_PIN_INVITE_WORK_ITEM_COUNT;
  }

  public createChatPinvite(pinType: PinType): Promise<WorkItemChatPinInvite> {
    if (!this.canCreatePinvite()) {
      throw new Error('Cannot create chat pin invite!');
    }

    return this.bcRestApi.createNewChatPinInvite(pinType)
      .then(pinvite => {
        const newPinviteWorkItem: WorkItemChatPinInvite = <WorkItemChatPinInvite>WorkItemFactory.create(WorkItemTypes.CHAT_PIN_INVITE, pinvite);
        this.chatPinviteWorkItemList.push(newPinviteWorkItem);

        return newPinviteWorkItem;
      });
  }

  public removePendingChatPinvite(pinInvitationId: string): Promise<WorkItemChatPinInvite | void> {
    const workItem: WorkItemChatPinInvite = this.chatPinviteWorkItemList.find((item: WorkItemChatPinInvite) => item.id === pinInvitationId);

    if (!workItem || workItem.isExpired()) {
      this.removeChatPinviteFromListLocally(workItem);
      return Promise.resolve(workItem);
    }

    return this.bcRestApi.removePendingChatPinInvite(pinInvitationId)
      .then(() => {
        this.removeChatPinviteFromListLocally(workItem);
      })
      .catch(() => {
        // Also remove workItem even if the pin invite is already used
        this.removeChatPinviteFromListLocally(workItem);
      }).then(() => workItem);
  }

  public removeChatPinviteFromListLocallyByInvitationId(pinInvitationId: string): WorkItemChatPinInvite {
    const workItem: WorkItemChatPinInvite = this.chatPinviteWorkItemList.find((item: WorkItemChatPinInvite) => item.id === pinInvitationId);
    this.removeChatPinviteFromListLocally(workItem);

    return workItem;
  }

  public removeChatPinviteFromListLocally(workItem: WorkItemChatPinInvite): void {
    if (!workItem) {
      return;
    }

    const pinInvitationWorkItemIndex = this.chatPinviteWorkItemList.indexOf(workItem);
    if (pinInvitationWorkItemIndex === -1) {
      return;
    }

    this.chatPinviteWorkItemList.splice(pinInvitationWorkItemIndex, 1);
  }

  private _preparePinvites() {
    const isPinInvitationEnabled = this.userPermissionService.hasPermissionFor(ActionPermissionType.PINVITE_ENABLED);
    if (!isPinInvitationEnabled) {
      return;
    }

    this.settingsService.getSettings().then((settings: Settings) => {
      this._isPinInvitationIsConfiguredForAccount = settings.invite && settings.invite.pinviteSettings && settings.invite.pinviteSettings.configured;

      if (this._isPinInvitationIsConfiguredForAccount) {
        this._loadChatPinvites();
      }
    });
  }

  private _loadChatPinvites(): Promise<void> {
    return this.bcRestApi.getPendingChatPinInvites()
      .then(pinvites => {
        const workItems = pinvites
          .map(pinvite => WorkItemFactory.create(WorkItemTypes.CHAT_PIN_INVITE, pinvite))
          .filter(item => !item.isExpired());

        this.chatPinviteWorkItemList.push.apply(this.chatPinviteWorkItemList, workItems);
      });
  }
}
